import { useEffect, useRef, useState } from 'react';
import DOMPurify, { type Config } from 'dompurify';
import type {
  BrandedHeaderData,
  BrandedHeaderProps,
  HeaderHtmlGroup,
} from './types.js';
import s from './BrandedHeader.module.css';

const HEADER_PURIFY_CONFIG: Config = {
  ADD_ATTR: ['target', 'class', 'style', 'type', 'href', 'rel', 'alt', 'title'],
  ADD_TAGS: ['style'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORCE_BODY: true,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: [
    'onerror',
    'onload',
    'onclick',
    'onmouseover',
    'onmouseout',
    'onmousemove',
    'onmouseenter',
    'onmouseleave',
  ],
  SAFE_FOR_TEMPLATES: true,
};

const renderHeaderHtml = (shadowRoot: ShadowRoot, html: string) => {
  const containerDiv = document.createElement('div');
  containerDiv.setAttribute('class', 'header-html-container');
  containerDiv.innerHTML = DOMPurify.sanitize(html, HEADER_PURIFY_CONFIG);

  const existingContainer = shadowRoot.querySelector('.header-html-container');
  if (existingContainer) {
    existingContainer.remove();
  }

  shadowRoot.appendChild(containerDiv);
};

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

const isSafeHref = (href: string): boolean => {
  try {
    return ALLOWED_PROTOCOLS.includes(new URL(href).protocol);
  } catch {
    return ALLOWED_PROTOCOLS.some((p) => href.startsWith(p));
  }
};

const isRenderableLink = (link: {
  label?: string | null;
  href?: string | null;
}) => {
  const href = link?.href?.trim();
  return !!href && !!link?.label?.trim() && isSafeHref(href);
};

const hasStructuredHeader = (
  header: BrandedHeaderData | null | undefined,
): boolean =>
  !!header &&
  (!!header.logo?.src ||
    (Array.isArray(header.links) && header.links.some(isRenderableLink)));

function isHeaderHtmlGroup(
  x: string | HeaderHtmlGroup | null | undefined,
): x is HeaderHtmlGroup {
  return (
    typeof x === 'object' &&
    x !== null &&
    ('BASE' in x || 'DESKTOP' in x || 'MOBILE' in x)
  );
}

const getHtmlToRender = (
  headerHtml: BrandedHeaderProps['headerHtml'],
  isMobile: boolean,
): string => {
  if (!headerHtml) return '';
  if (typeof headerHtml === 'string') return headerHtml;
  if (isHeaderHtmlGroup(headerHtml)) {
    return isMobile
      ? headerHtml.MOBILE || headerHtml.BASE || ''
      : headerHtml.DESKTOP || headerHtml.BASE || '';
  }
  return '';
};

const StructuredHeaderView = ({ header }: { header: BrandedHeaderData }) => {
  const layout = header.layout ?? 'LOGO_LEFT_NAV_RIGHT';

  const logoHref = header.logo?.href?.trim();
  const hasLogoLink = !!logoHref && isSafeHref(logoHref);
  const renderableLinks = (header.links ?? []).filter(isRenderableLink);

  return (
    <header
      className={`${s.header} ${header.fixed ? s.fixed : ''}`}
      data-layout={layout}
      data-testid="branded-header-structured"
    >
      <div className={s.logoSlot}>
        {header.logo?.src &&
          (hasLogoLink && logoHref ? (
            <a
              className={s.logoLink}
              href={logoHref}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt=""
                className={s.logoImg}
                src={header.logo.src}
                loading="lazy"
              />
            </a>
          ) : (
            <img
              alt=""
              className={s.logoImg}
              src={header.logo.src}
              loading="lazy"
            />
          ))}
      </div>
      <nav className={s.navSlot} aria-label="Header navigation">
        <ul className={s.navList}>
          {renderableLinks.map((link) => {
            const href = link.href!.trim();
            const label = link.label!.trim();
            return (
              <li key={`${href}-${label}`} className={s.navItem}>
                <a
                  className={s.navLink}
                  href={href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </header>
  );
};

/**
 * Branded header: structured (logo + nav + layout/colors) or raw HTML in shadow DOM.
 * All data is passed via props; no app-specific hooks.
 */
export const BrandedHeader = ({
  header,
  headerHtml,
  isMobile = false,
}: BrandedHeaderProps) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

  const htmlToRender = getHtmlToRender(headerHtml, isMobile);

  useEffect(() => {
    if (shadowRoot && htmlToRender) {
      renderHeaderHtml(shadowRoot, htmlToRender);
    } else if (shadowRoot && !htmlToRender) {
      const existing = shadowRoot.querySelector('.header-html-container');
      if (existing) existing.remove();
    } else if (hostRef.current && !shadowRoot) {
      setShadowRoot(hostRef.current.attachShadow({ mode: 'open' }));
    }
  }, [shadowRoot, htmlToRender]);

  if (hasStructuredHeader(header)) {
    return <StructuredHeaderView header={header!} />;
  }

  if (!headerHtml || !htmlToRender) return null;

  return <div ref={hostRef} data-testid="branded-header-host" />;
};
